import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface AuditRow {
  readonly auditId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly action: string;
  readonly actorId: string | null;
  /** Who acted, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly eventTimestamp: string;
}

const ENTITY_TYPES = [
  '', 'T323AiModel', 'T324ModelVersion', 'T326ModelDeployment', 'T327PredictionResult',
  'T329Recommendation', 'T330RiskScore', 'T331Anomaly', 'T332FraudAlert', 'T333ModelEvaluation',
];

/**
 * M25 — AI Audit Dashboard (§30, BR-07).
 * Read-only window onto the append-only AI audit trail. Every model change,
 * prediction, recommendation, risk score and fraud action is recorded with its
 * actor and timestamp, making all AI actions fully traceable.
 */
export function M25AiAuditDashboardPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [entityType, setEntityType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const query = entityType ? `?entityType=${encodeURIComponent(entityType)}` : '';
    apiRequest<{ data: AuditRow[]; total: number }>(`/v1/ai/audit-logs${query}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, entityType]);

  return (
    <main aria-label="ai-audit-dashboard">
      <h1>{tr('m25.aiAudit.title')}</h1>
      <label htmlFor="audit-entity-filter">{tr('m25.aiAudit.filterLabel')}</label>
      <select id="audit-entity-filter" value={entityType} onChange={(e) => setEntityType(e.target.value)}>
        {ENTITY_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m25.aiAudit.allEntities')}</option>)}
      </select>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m25.aiAudit.loading')}</p> : (
        <>
          <p aria-label="audit-count">{tr('m25.aiAudit.count', { count: total })}</p>
          <table aria-label="audit-table">
            <thead>
              <tr><th>{tr('m25.aiAudit.colEvent')}</th><th>{tr('m25.aiAudit.colEntity')}</th><th>{tr('m25.aiAudit.colAction')}</th><th>{tr('m25.aiAudit.colActor')}</th><th>{tr('m25.aiAudit.colTimestamp')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.auditId}>
                  <td>{r.eventType}</td>
                  <td>{r.entityType}</td>
                  <td>{r.action}</td>
                  <td>{personLabel(r.actorName, r.actorCode) ?? '—'}</td>
                  <td>{at(r.eventTimestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
